import {Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {HttpClient} from "@angular/common/http";

import {AppAlertService} from "../app-alert.service";
import {MenuService} from "../menu.service";
import {ParametersService} from "../parameters.service";
import {SettingsService} from "./settings.service";
import {SubtitlesService} from "../subtitles.service";
import {UserService} from "../user.service";
import {
    MenuElement,
    MenuResponse,
    ModuleData, PasswordMenuElementsData
} from "../../models";
import {Observable} from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class SettingsSaveService {
    private allElementsMenu: Array<MenuElement> = [];
    private currentElementIndex: number = -1;
    inputTitle: string = '';
    loader: boolean = false;
    menuPromise: Observable<Array<MenuResponse>>;
    private moduleIndex: number = -1;
    private pdfQueryList = new Set<string>();
    private renderer: Renderer2;
    showInput: boolean = false;
    private title: string = '';

    constructor(
        private appAlert: AppAlertService,
        private http: HttpClient,
        private menu: MenuService,
        private parameters: ParametersService,
        rendererFactory: RendererFactory2,
        private settings: SettingsService,
        private subtitles: SubtitlesService,
        private user: UserService,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    makePdf(selectedModule: ModuleData): void {
        this.loader = true;
        this.moduleIndex = selectedModule.id;
        this.showInput = false;
        const udid = this.getSelectedUdid();
        this.menuPromise = this.menu.getMenuPromise(udid);

        this.getMenuElementsAndParents();
    }

    searchPasswordedElements(): void {
        this.showInput = false;
        let isMenuReady = true;
        const udid = this.getSelectedUdid();

        for (let i = 0; i < this.allElementsMenu.length; i++) {
            this.currentElementIndex = -1;
            const element = this.allElementsMenu[i];

            if (Object.keys(element).length === 0) {
                continue;
            }

            const savedPinData = this.menu.getPinData(element, udid);
            if (savedPinData) {
                const queryString = `${savedPinData.menuType}:${savedPinData.id}:${savedPinData.pin}`;
                this.pdfQueryList.add(queryString);
            }

            if (this.menu.hasParamsPassword(element) && element.params.password === 'password') {
                this.inputTitle = this.subtitles.setSubtitle(3505) + ' ' + this.subtitles.setSubtitle(element.txtId);
                element.params.password = 'validate';
                this.allElementsMenu[i] = element;
                this.currentElementIndex = i;

                setTimeout(() => {
                    this.showInput = true;
                }, 0);

                isMenuReady = false;
                break;
            }
        }

        if (isMenuReady) {
            this.menu.passwordQuery = [];

            let udid = this.getSelectedUdid();
            this.menu.pins = this.menu.pins.filter(pinData => pinData.udid !== udid || (pinData.udid === udid && pinData.id === 0));

            if (this.allElementsMenu.length === 0) {
                this.appAlert.openAlertDialog({
                    title: this.subtitles.setSubtitle(13914),
                    message: this.subtitles.setSubtitle(105),
                    okButtonText: this.subtitles.setSubtitle(5015),
                });
            } else {
                this.getPdfFile();
            }
        }
    }

    getPdfFile(): void {
        const udid = this.getSelectedUdid();
        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${udid}`;
        let tag = '';
        const selectedTag = this.getSelectedModuleTag();
        if (selectedTag) {
            tag = `/tag/${selectedTag}`;
        }
        const query = '/' + [...this.pdfQueryList].join(',');

        this.http.get(`${baseUrl}/pdf/lang/${this.subtitles.language}${tag}${query}`, {responseType: 'blob'})
            .subscribe({
                next: (response) => {
                    const file = new Blob([response], {type: 'application/pdf'});
                    const fileURL = URL.createObjectURL(file);

                    const downloadLink = this.renderer.createElement('a');
                    this.renderer.setAttribute(downloadLink, 'href', fileURL);
                    this.title = this.parameters.modules[this.moduleIndex].name;
                    this.renderer.setAttribute(downloadLink, 'download', this.title + '.pdf');
                    this.renderer.appendChild(document.body, downloadLink);
                    downloadLink.click();
                    this.renderer.removeChild(document.body, downloadLink);
                    this.title = '';
                    this.loader = false;
                    this.pdfQueryList.clear();
                },
                error: () => {
                    this.pdfQueryList.clear();
                    this.appAlert.showAppAlert(this.subtitles.setSubtitle(7891), this.subtitles.setSubtitle(475), null, false, null);
                }
            });
    }

    getMenuElementsAndParents(): void {
        this.menuPromise.subscribe((responses) => {
            const { allElementMenu } = this.menu.getMenuElements(responses);
            this.allElementsMenu = [];

            for (const val of allElementMenu) {
                if (Object.keys(val).length > 0) {
                    this.allElementsMenu.push(val);
                }
            }

            this.searchPasswordedElements();
        })
    }

    getSelectedUdid(): string | 0 {
        let udid = this.parameters.selectedModuleHash;
        if (this.parameters.selectedModuleHash !== this.settings.selectedModule.udid) {
            udid = this.settings.selectedModule.udid;
        }
        return udid;
    }

    getMenuParentElement(element: MenuElement): MenuElement | undefined {
        return this.allElementsMenu.find(menuElement =>
            menuElement.id === element.parentId && menuElement.menuType === element.menuType
        )
    }

    getPasswordedElements(): void {
        if (this.menu.password === '') {
            this.appAlert.showAppAlert(this.subtitles.setSubtitle(4074), this.subtitles.setSubtitle(1136), null, false, null);
            return;
        }

        let udid = this.getSelectedUdid();
        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${udid}`;

        const element = this.allElementsMenu[this.currentElementIndex];
        const {menuType} = element;

        let query = '';
        let nextQueryString = '';
        if (element.id === 0) {
            query = this.menu.createMenuQuery(menuType, udid);

            if (query === menuType) {
                query = `${menuType}/0:${this.menu.password}`
            }
            nextQueryString = query.split('').map((ch: string) => ch === "/" ? ":" : ch).join('');
        } else {
            const menuElementParent = this.getMenuParentElement(element);
            query = `${menuType}/${this.menu.createMenuElementQuery(element, udid, menuElementParent)}`;
            const queryArray = query.split(",");
            const queryElement = queryArray[queryArray.length - 1];
            nextQueryString = `${menuType}:${queryElement}`;
        }

        this.http.get<PasswordMenuElementsData>(`${baseUrl}/menu/${query}`).subscribe({
            next: (response) => {
                this.showInput = false;
                this.menu.addPin(element, udid);

                if (response.data.elements.length !== 0) {
                    // It set menu elements in specific order
                    const tempMenu = {
                        MI: this.allElementsMenu.filter(element => element.menuType === 'MI'),
                        MS: this.allElementsMenu.filter(element => element.menuType === 'MS'),
                        MU: this.allElementsMenu.filter(element => element.menuType === 'MU'),
                        MP: this.allElementsMenu.filter(element => element.menuType === 'MP'),
                    }
                    tempMenu[menuType] = response.data.elements
                    this.allElementsMenu = [].concat(tempMenu.MI, tempMenu.MS, tempMenu.MU, tempMenu.MP);

                    const elementIndex = this.allElementsMenu.findIndex(newElement =>
                        newElement.id === element.id && newElement.menuType === element.menuType
                    );
                    const newElement = this.allElementsMenu[elementIndex];
                    if (this.menu.hasParamsPassword(newElement)) {
                        newElement.params.password = '';
                        this.allElementsMenu[elementIndex] = newElement
                    }
                    this.pdfQueryList.add(nextQueryString);
                } else {
                    if (this.menu.hasParamsPassword(element)) {
                        element.params.password = 'password';
                    }
                }

                this.menu.password = '';
                this.searchPasswordedElements();
            },
            error: (response) => {
                if (response.status === 422) {
                    this.menu.password = '';
                    this.menu.passwordQuery = this.menu.passwordQuery.filter(query => {
                        const queryId = parseInt(query.split(':')[0]);

                        return queryId !== element.id
                    });
                    this.appAlert.openAlertDialog({
                        title: this.subtitles.setSubtitle(13914),
                        message: this.subtitles.setSubtitle(4074),
                        okButtonText: this.subtitles.setSubtitle(1136),
                    })
                }
            }
        });
    }

    getSelectedModuleTag(): string {
        const selectedUdid = this.getSelectedUdid();
        let selectedModule: ModuleData | null = null;
        if (this.parameters.modules.length) {
            selectedModule = this.parameters.modules.find(module => module.udid === selectedUdid);
        }
        return selectedModule ? selectedModule.tag : '';
    }
}
